import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { SITE } from "@/constants/testIds";

const NAV = [
  { label: "About", href: "#about", id: SITE.navAbout },
  { label: "Care Pillars", href: "#services", id: SITE.navServices },
  { label: "Process", href: "#process", id: SITE.navProcess },
  { label: "Pricing", href: "#pricing", id: "site-nav-pricing" },
  { label: "Packages", href: "#packages", id: "site-nav-packages" },
  { label: "Stories", href: "#testimonials", id: SITE.navTestimonials },
  { label: "FAQ", href: "#faq", id: SITE.navFaq },
];

export const Header = ({ phone, whatsapp, onCallbackClick }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // `onScroll` is scoped inside the effect and `setScrolled` is a stable setter — no external deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-cream/80 backdrop-blur-xl border-b border-line/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid={SITE.navLogo}
          className="group flex items-baseline gap-2"
        >
          <span className="font-serif text-3xl text-ink tracking-tight">ReGain</span>
          <span className="hidden sm:inline text-[11px] uppercase tracking-[0.3em] text-ink-muted">
            Virtual clinic
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <a
              key={n.label}
              href={n.href}
              data-testid={n.id}
              className="text-sm text-ink-muted hover:text-sage transition-colors duration-200"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid={SITE.navCallbackBtn}
            onClick={onCallbackClick}
            className="hidden md:inline-flex text-sm text-ink hover:text-sage transition-colors px-3 py-2"
          >
            Request Callback
          </button>
          <a
            href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, "")}?text=${encodeURIComponent("Hi ReGain team, I'd like to know more about your virtual clinic.")}`}
            target="_blank"
            rel="noreferrer"
            data-testid={SITE.navWhatsappBtn}
            className="hidden md:inline-flex items-center gap-2 rounded-full border border-sage/30 text-sage hover:bg-sage hover:text-cream px-4 py-2.5 text-sm font-medium transition-all duration-300"
            aria-label="WhatsApp the doctor"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
            WhatsApp
          </a>
          <a
            href={`tel:${phone}`}
            data-testid={SITE.navCallBtn}
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-dark text-cream px-5 py-2.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 shadow-sm hover:shadow-md"
          >
            <Phone className="w-4 h-4" strokeWidth={1.75} />
            Call Now
          </a>
          <button
            className="lg:hidden p-2 -mr-2 text-ink"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-cream border-t border-line">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <a
                key={n.label}
                href={n.href}
                onClick={() => setOpen(false)}
                className="text-base text-ink hover:text-sage py-2"
              >
                {n.label}
              </a>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                onCallbackClick();
              }}
              className="text-left text-base text-ink py-2"
            >
              Request Callback
            </button>
            <a
              href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, "")}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-sage text-sage px-5 py-3 text-sm font-medium"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp {whatsapp.replace("+91", "+91 ")}
            </a>
            <a
              href={`tel:${phone}`}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-terracotta text-cream px-5 py-3 text-sm font-medium"
            >
              <Phone className="w-4 h-4" /> Call {phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
